// Cartao de conexao do canal Mercado Livre (plano canal-ml, F7), dentro da lista de canais.
// Mostra o estado (conectado como X / desconectado / vence em), o botao Conectar ao Mercado
// Livre (leva ao login do ML e volta para /admin/canais), Trocar Client Secret e o Mapa de
// categorias. Nunca mostra token nem segredo: a API de status nao os devolve.
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, KeyRound, Map as MapIcon } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { describeMlConnection, modelLabel, type MlStatus } from "../../lib/ml-channel";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { StatusDot } from "../ui/StatusDot";
import { MlSecretDialog } from "./MlSecretDialog";
import { MlCategoryMapDialog } from "./MlCategoryMapDialog";

const DOT_TONE = { ok: "good", warn: "warn", bad: "bad", idle: "neutral" } as const;

export function MlChannelCard({ slug, name }: { slug: string; name: string }): JSX.Element {
  const { can } = useAuth();
  const [secretOpen, setSecretOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const status = useQuery<MlStatus>({
    queryKey: ["ml-status", slug],
    queryFn: async () => (await api.get<MlStatus>(`/api/admin/channels/${slug}/ml/status`)).data,
  });

  const connect = useMutation({
    mutationFn: async () => (await api.get<{ url: string }>(`/api/admin/channels/${slug}/ml/oauth/start`)).data,
    onSuccess: (d) => {
      // Sai para o login do Mercado Livre; o ML devolve para /admin/canais?ml=...
      window.location.assign(d.url);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (status.isLoading) return <Skeleton className="h-16 w-full" />;
  if (status.isError || !status.data) {
    return (
      <p className="field__hint" style={{ color: "var(--color-warn)" }}>
        Não deu para ler o estado da conexão com o Mercado Livre ({apiErrorMessage(status.error)}).
      </p>
    );
  }
  const st = status.data;
  const view = describeMlConnection(st, Date.now());
  const lastErr = st.last_error?.at ? new Date(st.last_error.at).toLocaleString("pt-BR") : null;

  return (
    <div
      className="stack"
      style={{
        gap: ".5rem",
        padding: ".625rem .75rem",
        borderRadius: "var(--radius-md)",
        background: "var(--color-soft)",
        border: ".0625rem solid var(--color-line)",
      }}
      aria-label={`Conexão do canal ${name} com o Mercado Livre`}
    >
      <div style={{ minWidth: 0 }}>
        {/* Ponto e titulo na MESMA linha, centralizados entre si (sem padding em px para "alinhar no olho").
            O titulo ja e texto visivel: o ponto e decorativo e nao repete o rotulo para leitor de tela. */}
        <div className="cluster" style={{ alignItems: "center" }}>
          <StatusDot tone={DOT_TONE[view.tone]} />
          <span style={{ fontWeight: 800 }}>{view.title}</span>
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="field__hint">{view.detail}</div>
          <div className="field__hint">Modelo da conta: {modelLabel(st.model)}</div>
          {st.connection === "desconectado" && lastErr ? (
            <div className="field__hint" style={{ color: "var(--color-bad)" }}>Desconectou em {lastErr}.</div>
          ) : null}
        </div>
      </div>
      <div className="cluster">
        {view.action ? (
          <Button
            size="sm"
            onClick={() => connect.mutate()}
            disabled={view.needsSecret || connect.isPending}
            loading={connect.isPending}
          >
            <Link2 className="icon" aria-hidden /> {view.action}
          </Button>
        ) : null}
        <Button size="sm" variant="secondary" onClick={() => setSecretOpen(true)}>
          <KeyRound className="icon" aria-hidden /> {st.has_client_secret ? "Trocar Client Secret" : "Informar Client Secret"}
        </Button>
        {can("channel_rules.write") ? (
          <Button size="sm" variant="secondary" onClick={() => setMapOpen(true)}>
            <MapIcon className="icon" aria-hidden /> Mapa de categorias
          </Button>
        ) : null}
      </div>
      {secretOpen ? <MlSecretDialog slug={slug} hasSecret={st.has_client_secret} open={secretOpen} onOpenChange={setSecretOpen} /> : null}
      {mapOpen ? <MlCategoryMapDialog slug={slug} name={name} open={mapOpen} onOpenChange={setMapOpen} /> : null}
    </div>
  );
}
